import { RyuBreak, RyuGrid, RyuText, RyuButton, RyuInputText, RyuInputCheckboxStack } from '@ramp/ryu'
import { UseFormReturn, Controller } from 'react-hook-form'
import { Fragment } from 'react'

import { LeadModel } from '~/src/models/leads/LeadModel'
import {
  INTERESTED_PRODUCTS_LABEL_MAP,
  PossibleInterestedProducts,
} from '~/src/components/UsernameSignUp'
import { SalesDemoForm } from '~/src/routes/UserQualification/pages/sales-demo-type'
import { getAccountsReceivableInterestOptions } from '~/src/utils/accountsReceivableInterest'

interface Props {
  lead: LeadModel | null
  form: UseFormReturn<SalesDemoForm>
  onSubmit: (data: SalesDemoForm) => void
  hideUkUnavailableProducts?: boolean
}

export function SalesDemoFormInputs({ lead, form, onSubmit, hideUkUnavailableProducts = false }: Props) {
  const { handleSubmit, control } = form

  return (
    <div>
      {lead?.company_email && lead.company_email.length > 0 && (
        <RyuInputText variant='outlined' label='Email' value={lead.company_email} disabled={true} />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <RyuBreak size='s' />

        <RyuGrid columns={2} responsiveColumns={1} gapSize='s'>
          <div>
            <Controller
              name='first_name'
              control={control}
              rules={{ required: 'First name is required' }}
              render={({ field, fieldState }) => (
                <Fragment>
                  <RyuInputText
                    {...field}
                    value={field.value || ''}
                    variant='outlined'
                    label='First Name'
                    hasError={!!fieldState.error}
                  />
                  {fieldState.error && (
                    <Fragment>
                      <RyuBreak size='xs' />
                      <RyuText color='destructive' size='xs'>
                        {fieldState.error.message}
                      </RyuText>
                    </Fragment>
                  )}
                </Fragment>
              )}
            />
          </div>
          <div>
            <Controller
              name='last_name'
              control={control}
              rules={{ required: 'Last name is required' }}
              render={({ field, fieldState }) => (
                <Fragment>
                  <RyuInputText
                    {...field}
                    value={field.value || ''}
                    variant='outlined'
                    label='Last Name'
                    hasError={!!fieldState.error}
                  />
                  {fieldState.error && (
                    <Fragment>
                      <RyuBreak size='xs' />
                      <RyuText color='destructive' size='xs'>
                        {fieldState.error.message}
                      </RyuText>
                    </Fragment>
                  )}
                </Fragment>
              )}
            />
          </div>
        </RyuGrid>

        <RyuBreak size='s' />

        <Controller
          name='company'
          control={control}
          rules={{ required: 'Company name is required' }}
          render={({ field, fieldState }) => (
            <Fragment>
              <RyuInputText
                {...field}
                value={field.value || ''}
                variant='outlined'
                label='Company Name'
                hasError={!!fieldState.error}
              />
              {fieldState.error && (
                <Fragment>
                  <RyuBreak size='xs' />
                  <RyuText color='destructive' size='xs'>
                    {fieldState.error.message}
                  </RyuText>
                </Fragment>
              )}
            </Fragment>
          )}
        />

        <RyuBreak size='s' />

        <Controller
          name='interested_in_products'
          control={control}
          rules={{
            validate: (value) => {
              if (!value || value.length === 0) {
                return 'Select at least one option'
              }

              return true
            },
          }}
          render={({ field, fieldState }) => (
            <RyuInputCheckboxStack<PossibleInterestedProducts>
              legend={
                <RyuText.span size='s' color='hushed'>
                  Products you're interested in
                </RyuText.span>
              }
              error={fieldState.error?.message}
              caption="Don't worry. This is not a commitment to any product."
              items={[
                {
                  label: INTERESTED_PRODUCTS_LABEL_MAP.interested_in_corporate_cards,
                  value: 'interested_in_corporate_cards',
                },
                {
                  label: INTERESTED_PRODUCTS_LABEL_MAP.interested_in_bill_payments,
                  value: 'interested_in_bill_payments',
                },
                ...(!hideUkUnavailableProducts
                  ? [
                      {
                        label: INTERESTED_PRODUCTS_LABEL_MAP.interested_in_procurement,
                        value: 'interested_in_procurement' as const,
                      },
                      {
                        label: INTERESTED_PRODUCTS_LABEL_MAP.interested_in_treasury,
                        value: 'interested_in_treasury' as const,
                      },
                    ]
                  : []),
                ...getAccountsReceivableInterestOptions(),
                {
                  label: INTERESTED_PRODUCTS_LABEL_MAP.interested_in_stack,
                  value: 'interested_in_stack',
                },
                {
                  label: INTERESTED_PRODUCTS_LABEL_MAP.interested_in_token_spend_management,
                  value: 'interested_in_token_spend_management',
                },
              ]}
              values={field.value || []}
              onChange={field.onChange}
            />
          )}
        />

        <RyuBreak size='xl' />

        <RyuButton variant='prime' color='primary' type='submit' fullWidth={true} _textAlign='center'>
          Book a demo
        </RyuButton>
      </form>
    </div>
  )
}
